import type {
	LandSignal,
	LandSignalTier,
} from "@/lib/personalization/land-signal";

/**
 * Visual contract for the Land Signal pill — the 4-tier colour-coded
 * hireability indicator shown on every job card. Always paired with a
 * text label + per-tier icon so the signal survives colour-blindness.
 */

type Variant = "compact" | "default";

/**
 * `tier` is the shipped four-hue treatment. `one-voice` is the `feed-badge-row`
 * v2 arm: DESIGN.md reserves colour for interactive elements and status, and
 * this row was carrying four unrelated hues at once (blue, emerald, amber,
 * rose here, plus orange on the urgency chip) in raw Tailwind palette
 * utilities. Under `one-voice` the two tiers worth acting on keep the accent
 * and the two that are not go quiet — a long-shot row measured a 0.00% apply
 * rate over 30 days and should not be the loudest thing on the card.
 *
 * Scoped to this prop rather than applied to TIER_VISUALS directly because the
 * homepage hero (`land-this-card.tsx`) renders the same component and is not
 * part of this experiment.
 */
type Tone = "tier" | "one-voice";

type LandSignalPillProps = {
	signal: LandSignal;
	label: string;
	ariaLabelTemplate?: string;
	variant?: Variant;
	tone?: Tone;
};

type TierVisual = {
	classes: string;
	icon: string;
};

const TIER_VISUALS: Record<LandSignalTier, TierVisual> = {
	"lock-in": {
		classes:
			"border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300",
		icon: "✓",
	},
	"strong-fit": {
		classes:
			"border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
		icon: "●",
	},
	stretch: {
		classes:
			"border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-300",
		icon: "◐",
	},
	"long-shot": {
		classes:
			"border-rose-500/40 bg-rose-500/5 text-rose-700/80 dark:text-rose-300/80",
		icon: "◌",
	},
};

/** The v2 arm's two-step ramp: accent for a tier worth acting on, quiet
 * otherwise. Both sides are semantic tokens, so dark mode is derived rather
 * than hand-patched. */
const ONE_VOICE_CLASSES: Record<LandSignalTier, string> = {
	"lock-in": "border-primary/30 bg-primary/10 text-primary",
	"strong-fit": "border-primary/30 bg-primary/10 text-primary",
	stretch: "border-border bg-muted/40 text-muted-foreground",
	"long-shot": "border-border bg-muted/40 text-muted-foreground",
};

function fillTemplate(
	template: string,
	values: Record<string, string>,
): string {
	return Object.entries(values).reduce(
		(acc, [key, value]) => acc.replaceAll(`{${key}}`, value),
		template,
	);
}

export function LandSignalPill({
	signal,
	label,
	ariaLabelTemplate,
	variant = "default",
	tone = "tier",
}: LandSignalPillProps) {
	const visual = TIER_VISUALS[signal.tier];
	const toneClasses =
		tone === "one-voice" ? ONE_VOICE_CLASSES[signal.tier] : visual.classes;
	const sizeClasses =
		variant === "compact"
			? "px-1.5 py-0.5 text-xs gap-1"
			: "px-2 py-0.5 text-xs gap-1.5";
	const ariaLabel = ariaLabelTemplate
		? fillTemplate(ariaLabelTemplate, {
				tier: label,
				score: String(signal.score),
			})
		: `${label} (${signal.score}/100)`;

	return (
		<span
			role="img"
			aria-label={ariaLabel}
			data-testid="land-signal-pill"
			data-tier={signal.tier}
			data-source={signal.source}
			className={`inline-flex items-center rounded-full border font-semibold uppercase tracking-wide ${toneClasses} ${sizeClasses}`}
		>
			<span aria-hidden="true">{visual.icon}</span>
			<span>{label}</span>
		</span>
	);
}

/**
 * Map a Land Signal tier to the i18n key that holds its localized
 * label. Centralised here so callers stay in one place if we rename.
 */
export const TIER_LABEL_KEYS: Record<LandSignalTier, string> = {
	"lock-in": "landSignalLockIn",
	"strong-fit": "landSignalStrongFit",
	stretch: "landSignalStretch",
	"long-shot": "landSignalLongShot",
};
